import { memo, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from "react";
import type { FireSummary } from "../../shared/types";
import { FLARE_EXPLORER_URL } from "../../shared/constants";
import { formatCoverageBps, formatFlr, formatToken, formatUsdE18, truncateHash } from "../../shared/format";
import { ExternalLinkIcon } from "./Icons";
import flrLogo from "../assets/flr-logo.svg";
import fxrpLogo from "../assets/fxrp-logo.svg";
import usdLogo from "../assets/usd-logo.svg";

function balanceFor(source: FireSummary["sources"][number], symbol: string): bigint {
  return BigInt(source.balances.find((balance) => balance.symbol === symbol)?.amount ?? "0");
}

function AssetMark({ symbol }: { symbol: "FLR" | "FXRP" }) {
  return <img src={symbol === "FXRP" ? fxrpLogo : flrLogo} alt="" aria-hidden="true" />;
}

export const ContextPanels=memo(function ContextPanels({ fire }: { fire: FireSummary }) {
  const fireReady = fire.assets.length > 0;
  const fxrp = fire.assets.find((asset) => asset.symbol === "FXRP");
  const [selectedAddress, setSelectedAddress] = useState(fire.sources[0]?.address ?? "");
  const sourceTabs = useRef(new Map<string, HTMLButtonElement>());
  const sources = useMemo(() => {
    const prepared = fire.sources.map((source) => {
      const flrWei = balanceFor(source, "FLR") + balanceFor(source, "WFLR");
      const fxrpAmount = balanceFor(source, "FXRP");
      const fxrpDecimals = source.balances.find((balance) => balance.symbol === "FXRP")?.decimals ?? 6;
      const token = fxrpAmount > 0n || source.asset === "FXRP" ? "FXRP" as const : "FLR" as const;
      return { ...source, flrWei, fxrpAmount, fxrpDecimals, token, primaryAmount: token === "FXRP" ? fxrpAmount : flrWei };
    });
    const totals = {
      FLR: prepared.reduce((sum, source) => sum + (source.token === "FLR" ? source.primaryAmount : 0n), 0n),
      FXRP: prepared.reduce((sum, source) => sum + (source.token === "FXRP" ? source.primaryAmount : 0n), 0n),
    };
    return prepared.map((source) => ({
      ...source,
      shareBps: totals[source.token] ? (source.primaryAmount * 10_000n) / totals[source.token] : 0n,
    }));
  }, [fire.sources]);

  const selected = sources.find((source) => source.address === selectedAddress) ?? sources[0];
  const valuationDelayed=fire.valuation.flrUsd?.stale||fire.valuation.xrpUsd?.stale;
  const valuationStatus=fire.valuation.totalUsdE18===null ? "FTSO price unavailable" : valuationDelayed ? "FTSO pricing delayed" : "FTSO spot · FLR + FXRP";
  const moveSourceFocus = (event: KeyboardEvent<HTMLButtonElement>, address: string) => {
    if (!["ArrowDown", "ArrowRight", "ArrowUp", "ArrowLeft", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const current = Math.max(0, sources.findIndex((source) => source.address === address));
    const nextIndex = event.key === "Home"
      ? 0
      : event.key === "End"
        ? sources.length - 1
        : ["ArrowDown", "ArrowRight"].includes(event.key)
          ? (current + 1) % sources.length
          : (current - 1 + sources.length) % sources.length;
    const next = sources[nextIndex];
    if (!next) return;
    setSelectedAddress(next.address);
    window.requestAnimationFrame(() => sourceTabs.current.get(next.address)?.focus());
  };

  return (
    <div className="context-grid context-grid--fire">
      <section className="context-panel fire-panel" aria-labelledby="fire-title">
        <div className="fire-panel__heading">
          <div>
            <span className="eyebrow">FIP-16 revenue · verified collector wallets</span>
            <h2 id="fire-title">FIRE Reserves</h2>
            <p>Follow the live fee reservoirs for FDC requests, minting tags, and FXRP minting and redemption. Values are current collector balances—not assumed burns or lifetime inflows.</p>
          </div>
        </div>

        {fireReady ? <>
          <div className="fire-totals">
            <div className="fire-total fire-total--flr"><AssetMark symbol="FLR" /><span>FLR equivalent held</span><strong>{formatFlr(fire.flrHeldWei)}</strong><small>FLR + WFLR</small></div>
            <div className="fire-total fire-total--fxrp"><AssetMark symbol="FXRP" /><span>FXRP collected</span><strong>{fxrp ? formatToken(fxrp.amount, fxrp.decimals) : "0.00"}</strong><small>minting + redemption fees</small></div>
            <div className="fire-total fire-total--usd"><img src={usdLogo} alt="" aria-hidden="true"/><span>Total reserve value</span><strong>{formatUsdE18(fire.valuation.totalUsdE18)}</strong><small>{valuationStatus}</small></div>
          </div>

          <div className="fire-experience">
            <div className="fire-streams" role="tablist" aria-label="FIRE fee sources">
              <div className="fire-streams__label"><span>Revenue channels</span><small>Select a wallet to inspect</small></div>
              {sources.map((source) => {
                const amount = source.token === "FXRP" ? formatToken(source.primaryAmount, source.fxrpDecimals) : formatFlr(source.primaryAmount);
                const measured = source.primaryAmount > 0n;
                return <button
                  className="fire-stream"
                  type="button"
                  role="tab"
                  aria-selected={source.address === selected?.address}
                  aria-controls="fire-wallet-detail"
                  tabIndex={source.address === selected?.address ? 0 : -1}
                  key={source.address}
                  onClick={() => setSelectedAddress(source.address)}
                  onKeyDown={(event) => moveSourceFocus(event, source.address)}
                  ref={(node) => { if (node) sourceTabs.current.set(source.address, node); else sourceTabs.current.delete(source.address); }}
                  style={{ "--fire-share": `${Number(source.shareBps) / 100}%` } as CSSProperties}
                >
                  <span className="fire-stream__asset"><AssetMark symbol={source.token} /></span>
                  <span className="fire-stream__copy"><b>{source.label}</b><small>{measured ? `${amount} ${source.token}` : "Awaiting wallet snapshot"}</small></span>
                  <span className="fire-stream__share">{measured ? formatCoverageBps(source.shareBps.toString()) : "—"}<small>of {source.token}</small></span>
                  <i aria-hidden="true"><b /></i>
                </button>;
              })}
            </div>

            {selected && <article className="fire-wallet" id="fire-wallet-detail" role="tabpanel" aria-live="polite" key={selected.address}>
              <div className="fire-wallet__signal" aria-hidden="true"><span><AssetMark symbol={selected.token} /></span><i /><b /></div>
              <span className="eyebrow">Selected collector</span>
              <h3>{selected.label}</h3>
              <p>{selected.description}</p>
              <div className="fire-wallet__assets">
                {selected.balances.filter((balance) => BigInt(balance.amount) > 0n).map((balance) => (
                  <div key={balance.symbol}>
                    <span>{balance.symbol === "FXRP" ? <AssetMark symbol="FXRP" /> : <AssetMark symbol="FLR" />}{balance.symbol}</span>
                    <strong>{formatToken(balance.amount, balance.decimals)}</strong>
                  </div>
                ))}
                {!selected.balances.some((balance) => BigInt(balance.amount) > 0n) && <span className="fire-wallet__pending">The first per-wallet reserve snapshot is being collected.</span>}
              </div>
              <a className="fire-wallet__address" href={`${FLARE_EXPLORER_URL}/address/${selected.address}`} target="_blank" rel="noreferrer">
                <span>Open verified wallet</span><code>{truncateHash(selected.address, 8, 6)}</code><ExternalLinkIcon />
              </a>
            </article>}
          </div>
        </> : <p className="empty-state">Collecting the first verified reserve snapshot.</p>}

        <div className="fire-panel__footnote"><span>Reserves remain separate until a burn is proven.</span><span>Proven burned: <b>{formatFlr(fire.provenBurnedWei)} FLR</b></span><span>Balances refresh hourly.</span></div>
      </section>
    </div>
  );
});
